import { useQuery } from '@tanstack/react-query';

import { useGlobals } from '@shared/contexts/GlobalsContext';
import createQueryConfig from '../../queries';
import { useAppSelector } from './use-app-selector';
import type { Location } from '../types';

type SuspendableLocation = Pick<Location, 'suspended' | 'suspension_reason'>;

// Roles only cover active locations, so locations suspended for non-payment
// are counted separately: the user must still reach them to pay.
export function hasMultipleLocations({
  rolesCount,
  locations,
}: {
  rolesCount: number;
  locations: SuspendableLocation[];
}): boolean {
  const hasNonPaymentSuspendedLocation = locations.some(
    ({ suspended, suspension_reason }) =>
      suspended && suspension_reason === 'non_payment'
  );

  return rolesCount > 1 || hasNonPaymentSuspendedLocation;
}

export function useHasMultipleLocations(): boolean {
  const { queryClient, apiClient } = useGlobals();
  const rolesCount = useAppSelector(
    (state) => state.currentAccount?.roles?.length ?? 0
  );
  const organizationId = useAppSelector(
    (state) => state.currentAccount?.organizationId ?? ''
  );
  // Only needed when roles alone don't already show several locations.
  const { data } = useQuery({
    ...createQueryConfig(queryClient, apiClient).suspendedLocations(
      organizationId
    ),
    enabled: rolesCount <= 1 && Boolean(organizationId),
  });

  return hasMultipleLocations({
    rolesCount,
    // The JS client returns the requested attributes, including the
    // suspension_reason that @shore/types does not declare yet.
    locations: (data ?? []) as SuspendableLocation[],
  });
}
